import Head from "next/head";
import React, { useState, useEffect, useRef } from "react";
import Image from "next/image";
import { WEB_URL } from "@/config/endpoints";
import IMAGES from "@/public/assets/img";
import machineryFinancing from "@/config/machineryFinancing_config";
import { useRouter } from "next/router";
import ContactFormStep1 from "@/component/shared/contactForm/contactFormStep1";
import ContactFormStep2 from "@/component/shared/contactForm/contactFormStep2";
import { FormProvider } from "@/component/shared/contactForm/formContext";
import AccordianFaq from "@/component/shared/accordianfaq";
import HowItWorks from "@/component/dealerFinancing/howItWorks";
import ComparisonOptions from "@/component/shared/import-Factoring/comparisonOptions";
import ProgramResult from "@/component/dealerFinancing/programResult";
import IndustrialCapex from "@/component/dealerFinancing/industrialCapex";
import { generateFaqSchema } from "@/component/shared/faqSchema";
import TrustedSection from "@/component/dealerFinancing/trustedSection";

const MachineryLoan = () => {
  const router = useRouter();
  const [mWidth, setMWidth] = useState(0);
  const [showNextStep, setShowNextStep] = useState(false);
  const topSectionRef = useRef(null);
  const faqSchema = generateFaqSchema(machineryFinancing.Faq);


  const handleWindowResize = () => {
    setMWidth(window.innerWidth);
  };

  useEffect(() => {
    handleWindowResize();
    window.addEventListener("load", handleWindowResize);
    return () => window.removeEventListener("resize", handleWindowResize);
  }, []);

  const showSteps = () => {
    setShowNextStep(true);
  };
  
  const showFirstStep = () => {
    setShowNextStep(false);
  };

  return (
    <main className="pt-[110px]">
      <Head>
         <title>{machineryFinancing.seoData.title}</title>
          <meta name="description" content={machineryFinancing.seoData.description} />
          {/* OpenGraph tags */} 
          <meta property="og:title" content={machineryFinancing.seoData.title}/>
          <meta property="og:description" content={machineryFinancing.seoData.description} />
          <meta property="og:image" content={IMAGES.HOMEIMG.bigLogoNew.src} />
          <meta property="og:url" content={WEB_URL+router.asPath} />
          <link rel="canonical" href={WEB_URL+router.asPath} />
          <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(faqSchema),
          }}
        />
      </Head>

      <section
        ref={topSectionRef}
        className={`relative bg-no-repeat sm:bg-center bg-cover bg-bottom min-h-[450px] sm:min-h-[270px]`}
      >
         {mWidth > 768 ? 
        <Image
          priority={true}
          src={IMAGES.FreightLoan.bgBannerDesktop}
          width={1366}
          height={550}
          className="absolute inset-0 max-w-none w-full h-full"
          alt="Banner Desktop"
        /> :
        <Image
          priority={true}
          src={IMAGES.FreightLoan.bgBannerMobile}
          width={767}
          height={451}
          className="absolute inset-0 max-w-none w-full h-full"
          alt="Banner Mobile"
        />
      }
        <div className="container xm:px-2 relative px-3 flex justify-between items-center sm:flex-col pb-1 sm:pt-5">
          <div className="w-1/2 sm:w-full">
          <h1
              className="text-brand mb-3 sm:mb-2 md:text-3xl"
              dangerouslySetInnerHTML={{ __html: machineryFinancing.bannerHeading }}
            />
            <h2 className="text-base2 text-brand font-medium mb-1 sm:text-lg">{machineryFinancing.subheading}</h2>
            <p className="bg-[#E3A003] w-fit text-center py-xsp px-4 sm:px-1 rounded-lg mb-2 text-[22px] sm:text-sm text-white font-medium md:text-base2">
              {machineryFinancing.btntext}
            </p>
            <div className="flex gap-3 sm:gap-1 justify-between mt-4">
              {machineryFinancing.loanInfo.map((item, index) => (
                <div
                  key={index}
                  className="w-[32%]"
                >
                  <Image
                    src={item.icon}
                    alt={item.description}
                    width={80}
                    height={80}
                    className="mb-1 h-20 sm:h-16"
                  />
                  <p className="text-[#1A1A1A] text-sm sm:text-xs font-medium">
                    {item.description}
                  </p>
                </div>
              ))}
            </div>
          </div>
          <div className="w-2/5 sm:w-full pt-5 sm:pt-2 flex justify-center">
            <FormProvider>
              {!showNextStep ? (
                <ContactFormStep1 showStep1={showSteps} pageTitle='machinery-capex-financing' />
              ) : (
                <ContactFormStep2
                  showPrevStep={showFirstStep}
                  formType="GetInTouchNew"
                  pageTitle='machinery-capex-financing'
                />
              )}
            </FormProvider>
          </div>
        </div>
      </section>

      <TrustedSection trustData={machineryFinancing.benefits}/>
      <ComparisonOptions comparisonData={machineryFinancing.comparisonData}/>
      <ProgramResult data={machineryFinancing.eligibleMachinery} />
      <HowItWorks data={machineryFinancing.HowItWorks} />
      <IndustrialCapex data={machineryFinancing.industrialData}/>
      
      <section className="mt:py-sp-big mt:py-sp-big py-sp-100 sm:py-sp-big relative overflow-hidden bg-fixed" style={{ backgroundImage: `url( ${machineryFinancing?.ConuterSec?.bannerImg?.src})`, backgroundSize: 'cover' }}>
        {machineryFinancing.ConuterSec.bannerMbImage &&
          <Image src={machineryFinancing.ConuterSec.bannerMbImage} loading="lazy" className="sm:block z-[0] absolute hidden inset-0 w-auto h-full max-w-none" width={650} height={650} alt="stats bg" />
        }
        <div className='container z-[1] relative px-3 lg:px-3 xm:px-2'>
          <h2 className="mb-1 font-medium text-center text-white text-textxl sm:text-lg">{machineryFinancing.ConuterSec.heading}</h2>
          <div className="flex justify-between flex-wrap w-[80%] sm:w-full mx-auto">
            {machineryFinancing.ConuterSec.counterList.map((item, i) => (
              <div key={i} className="w-[22%] sm:w-full py-5 px-3">
                <h3 className="text-heading4 text-white text-center mb-xsp flex items-center justify-center">{item.heading}<strong className="text-textxl ml-xsp">{item.smalltext}</strong></h3>
                <p className="text-base font-medium text-center text-white">{item.paragraph}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Flags & Map starts*/}
      <section className="py-sp-100 sm:py-sp-big md:py-sp-big">
        <div className="container px-3 lg:px-3 xm:px-2">
          <div className="flex flex-wrap mx-auto">
            <div className="w-1/2 sm:w-full">
              <Image loading="lazy" src={IMAGES.HomeNew.worldMap} alt="World Map" className="w-[90%] h-auto sm:mb-5" width={525} height={280} />
            </div>
            <div className="w-1/2 sm:w-full">
              <h2 className="text-xxxl text-black mb-3  w-full sm:text-xl" dangerouslySetInnerHTML={{ __html: machineryFinancing.GlobalNetwork.heading }} />
              <p className="text-base text-secondary max-w-[600px] w-full font-regular">{machineryFinancing.GlobalNetwork.para}</p>
              <ul className="flex flex-wrap max-w-[500px] mt-10">
                {machineryFinancing.GlobalNetwork.flags.map((item, index) => (
                  <li key={index} className="mr-10 sm:w-[32%] sm:px-1 sm:mb-5 sm:mr-0 last:mr-0">
                    <Image loading="lazy" src={item.flag} alt={item.name + ' flag icon'} className="w-[44px] sm:w-[40px] h-auto mx-auto" width={44} height={44} />
                    <span className="block mt-1 text-base text-center">{item.name}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>
      {/* Flags & Map ends*/}

      <section className="bg-white pb-sp-100 sm:pb-sp-big md:pb-sp-big">
        <div className="container px-3 xm:px-2 ">
          <div>
            <h3 className="mb-6 font-bold text-center text-lg text-secondary">
              {machineryFinancing.FaqHead}
            </h3>
          </div>
          <div className="w-full text-left bg-white">
            <div className="mx-auto max-w-[1100px] sm:w-auto">
              <AccordianFaq faqData={machineryFinancing.Faq} />
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default MachineryLoan;
